import { useEffect, useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { fileUrl, workspaceApi } from "@/lib/catalog-api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui-shim";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

export default function WorkspaceSettings() {
  const { user, workspace, setWorkspacePartial } = useAuth();
  const isOwner = user?.role === "owner";
  const [name, setName] = useState(workspace?.name || "");
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    setName(workspace?.name || "");
  }, [workspace?.name]);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || name === workspace?.name) return;
    setBusy(true);
    try {
      const updated = await workspaceApi.update({ name: name.trim() });
      setWorkspacePartial({ name: updated.name });
      toast.success("Workspace updated.");
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="workspace-settings" className="space-y-12">
      <LogoSection />

      <section>
        <h2 className="font-display text-xl">Workspace</h2>
        <p className="text-sm text-muted-foreground mt-1">
          The name your team and clients see at the top of every shared link.
        </p>
        <form onSubmit={saveName} className="mt-4 space-y-4 max-w-md">
          <div>
            <Label htmlFor="ws-name" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Workspace name
            </Label>
            <Input id="ws-name" required
              data-testid="ws-name-input"
              value={name} onChange={(e: any) => setName(e.target.value)}
              className="mt-1.5 h-10 rounded-none" />
          </div>
          <Button type="submit" disabled={busy || !name.trim() || name === workspace?.name}
            data-testid="ws-save"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </Button>
        </form>
      </section>

      <section>
        <h2 className="font-display text-xl">Plan</h2>
        <p className="text-sm text-muted-foreground mt-1">Currently on <span className="font-mono">{workspace?.plan}</span>.</p>
        {workspace?.trial_ends_at && (
          <p className="text-xs text-muted-foreground mt-1">
            Trial ends {new Date(workspace.trial_ends_at).toLocaleDateString()}.
          </p>
        )}
      </section>

      {isOwner && (
        <section>
          <h2 className="font-display text-xl text-oxblood">Danger zone</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently archive this workspace. Items, clients, and loans become unrecoverable.
          </p>
          <Button variant="outline"
            onClick={() => setShowDelete(true)}
            data-testid="ws-delete-btn"
            className="mt-4 rounded-none border-oxblood/40 text-oxblood hover:bg-oxblood/5 h-10">
            Delete workspace…
          </Button>
        </section>
      )}

      {showDelete && (
        <DeleteDialog onClose={() => setShowDelete(false)} />
      )}
    </div>
  );
}

function LogoSection() {
  const { workspace } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null | undefined>(
    (workspace as any)?.logo_url,
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hydrate the latest logo from the API (auth context only carries name/plan).
  useEffect(() => {
    let alive = true;
    workspaceApi.get()
      .then((ws) => alive && setLogoUrl(ws.logo_url ?? null))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  function pick() {
    setError(null);
    inputRef.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;

    if (!ACCEPTED_LOGO_TYPES.has(f.type)) {
      setError("Logo must be PNG, JPEG, or WebP.");
      return;
    }
    if (f.size > MAX_LOGO_BYTES) {
      setError("Logo must be under 2 MB.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const r = await workspaceApi.uploadLogo(f);
      setLogoUrl(r.logo_url);
      toast.success("Logo updated.");
    } catch (e: any) {
      const m = e instanceof ApiError ? e.message : "Upload failed.";
      setError(m);
    } finally {
      setUploading(false);
    }
  }

  async function remove() {
    if (!logoUrl) return;
    if (!confirm("Remove the workspace logo?")) return;
    setUploading(true);
    try {
      await workspaceApi.removeLogo();
      setLogoUrl(null);
      toast.success("Logo removed.");
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not remove.");
    } finally {
      setUploading(false);
    }
  }

  const initials = (workspace?.name || "W")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "").join("");

  return (
    <section data-testid="logo-section">
      <h2 className="font-display text-xl">Logo</h2>
      <p className="text-sm text-muted-foreground mt-1">
        Shown in the header of every shared lookbook. PNG, JPG, or WebP — up to 2&nbsp;MB.
      </p>
      <div className="mt-5 flex items-center gap-5">
        <div className="w-20 h-20 grid place-items-center bg-bone overflow-hidden border border-border"
          data-testid="logo-preview">
          {logoUrl ? (
            <img src={fileUrl(logoUrl)} alt="Workspace logo"
              className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-2xl text-foreground/60 tracking-wider">{initials}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={inputRef} type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            data-testid="logo-file-input"
            onChange={onFile} />
          <Button type="button" onClick={pick} disabled={uploading}
            data-testid="logo-upload-btn"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo"}
          </Button>
          {logoUrl && (
            <Button type="button" variant="outline" onClick={remove} disabled={uploading}
              data-testid="logo-remove-btn"
              className="rounded-none border-foreground/20 hover:bg-bone h-10">
              <Trash2 className="w-4 h-4 mr-2" /> Remove
            </Button>
          )}
        </div>
      </div>
      {error && (
        <p className="mt-3 text-sm text-oxblood" data-testid="logo-error" role="alert">{error}</p>
      )}
    </section>
  );
}

function DeleteDialog({ onClose }: { onClose: () => void }) {
  const { workspace, logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const expected = workspace?.name || "";
  const ready = confirm.trim() === expected && password.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await workspaceApi.delete({
        workspace_name_confirmation: confirm.trim(),
        password,
      });
      toast.success("Workspace deleted.");
      await logout();
      window.location.href = "/";
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not delete.");
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-border" data-testid="ws-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-oxblood">Delete workspace</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Type <span className="font-mono text-foreground">{expected}</span> to confirm.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-3">
          <Input value={confirm} onChange={(e: any) => setConfirm(e.target.value)}
            data-testid="ws-delete-confirm"
            placeholder={expected}
            className="h-10 rounded-none" />
          <Input type="password" value={password} onChange={(e: any) => setPassword(e.target.value)}
            data-testid="ws-delete-password"
            placeholder="Your password"
            className="h-10 rounded-none" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}
              className="rounded-none border-foreground/20 hover:bg-bone h-10">Cancel</Button>
            <Button type="submit" disabled={!ready || busy}
              data-testid="ws-delete-confirm-btn"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 disabled:opacity-50">
              {busy ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
